import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import store from 'redux-store/store'
import { API, BASE_URL, ROLES_CORREDOR, RAMO_FECU_CREDITO_EXPORTACION_NEMOTECNICO } from '../../../../CONST';
import auth from '../../../../helpers/auth';
import CreditLineDetailTable from './CreditLineDetailTable';
import { get } from 'helpers/requests';
import { cellNumOperation } from '../../../../helpers/tables';
import { userHasRole } from '../../../../helpers/helpers';
import DownloadBtn from '../../../shared/DownloadBtn';
import WarningModal from '../../../shared/WarningModal';
import LoadingSpinner from '../../../shared/LoadingSpinner';
import moment from 'moment';
import isUserHasProfile from '../../../../helpers/profileValidator';
import { LINEA_CREDITO_WRITE } from '../../../../helpers/profilePermission';


const CreditLineDetail = ({ id, history }) => {
  const {user} = store.getState().login
  const [ linea, setLinea ] = useState(null);
  const [ clienteId, setClienteId ] = useState(null);
  const [ xlsxUrl, setXlsxUrl ] = useState('');
  const [ pdfUrl, setPdfUrl ] = useState('');
  const [showNotFoundModal, setShowNotFoundModal] = useState(false);
  const [terminoVigenciaPoliza, setTerminoVigenciaPoliza] = useState(null);
  const [lastEndosoCredito, setEndosoCredito] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [policyIsExportacion, setPolicyIsExportacion] = useState(false);
  const [isChilean, setIsChilean] = useState(true);
  

  useEffect(() => {
    const loadData = async () => {
      try {
        const url = `${BASE_URL}${API.simpleLineasCredito}/${id}`;
        const response = await auth.fetch(url);
        
        setLinea(response);
        setIsChilean(!response?.deudor_pais_code || response?.deudor_pais_code === 'CL');
        setClienteId(response.client_id);
        setPolicyIsExportacion(response.ramo_fecu_nemotecnico === RAMO_FECU_CREDITO_EXPORTACION_NEMOTECNICO);
        setXlsxUrl(`${BASE_URL}${API.movimientosLineaCredito}/xlsx/?linea_credito_id=${id}&client_id=${response.client_id}`);
        setPdfUrl(`${BASE_URL}${API.movimientosLineaCredito}/pdf/?linea_credito_id=${id}&client_id=${response.client_id}`);

        // Obtener término de vigencia de la póliza
        if (response.poliza_nemotecnico) {
          try {
            const polizaResponse = await get(`${BASE_URL}${API.fullPolizas}?client_id=${response.client_id}&nemotecnico=${response.poliza_nemotecnico}`);
            const polizaData = polizaResponse.results[0];
            if (polizaData) {
              setTerminoVigenciaPoliza(polizaData.termino_vigencia);
            }
          } catch (error) {
            console.error('Error loading poliza data:', error);
          }
        }

        setIsLoading(false);

        // Load endosos data
        if (response.poliza_id) {
          try {
            const endososCreditoUrl_borrador = `${BASE_URL}${API.simpleEndososCredito}/?poliza_id=${response.poliza_id}&get_last_including_borrador=true`;
            const endososCreditoUrl = `${BASE_URL}${API.simpleEndososCredito}/?poliza_id=${response.poliza_id}`;

            const [endososResponse, endososResponseIfBorrador] = await Promise.all([
              auth.fetch(response.client_id ? `${endososCreditoUrl}&client_id=${response.client_id}` : endososCreditoUrl),
              auth.fetch(response.client_id ? `${endososCreditoUrl_borrador}&client_id=${response.client_id}` : endososCreditoUrl_borrador),
            ]);
          
            if (endososResponseIfBorrador && endososResponseIfBorrador.length > 0) {
              const sortedByIdDesc = [...endososResponseIfBorrador].sort((a, b) => b.id - a.id);
              setEndosoCredito(sortedByIdDesc[0]);
            } else if (endososResponse && endososResponse.length > 0) {
              const sortedByIdDesc = [...endososResponse].sort((a, b) => b.id - a.id);
              setEndosoCredito(sortedByIdDesc[0]);
            }
          } catch (error) {
            console.error('Error loading endosos data:', error);
          }
        }
      } catch (error) {
        if (error.status === 404) {
          setShowNotFoundModal(true);
        }
        setIsLoading(false);
      }
    };

    loadData();
  }, [id]);

  const shouldEnableSolicitudButton = (!userHasRole(user, ROLES_CORREDOR) || user.is_orsan) && isUserHasProfile(LINEA_CREDITO_WRITE) &&
      linea && 
      moment(lastEndosoCredito.termino_vigencia).isAfter(moment()) &&
      lastEndosoCredito.estado !== 'construccion';

  return (
    <>
    <LoadingSpinner isLoading={isLoading} overlayHeight='25rem' />
    {!isLoading && (
      <>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '500px 500px',
            gap: '200px',
          }}
        >
          <div
            style={{
              display: 'grid', gridTemplateColumns: isChilean ? '100px auto' : '120px auto', gridGap: '15px 15px',
              padding: '0 0 16px 16px', alignItems: 'center',
            }}
          >
            <div className="orsan-label">Cliente:</div>
            <div className='orsan-value'>{linea ? linea.client_name : ''}</div>
            <div className="orsan-label">Póliza:</div>
            <div className='orsan-value'>{linea ? linea.poliza_nemotecnico : ''}</div>
            {!isChilean && (
              <>
                <div className='orsan-label'>País:</div>
                <div className='orsan-value'>{linea ? linea.deudor_pais_name : ''}</div>
              </>
            )}
            <div className="orsan-label">Razón Social:</div>
            <div className='orsan-value'>{linea ? linea.deudor_name : ''}</div>
            {isChilean ? (
              <>
                <div className='orsan-label'>RUT:</div>
                <div className='orsan-value'>{linea ? linea.deudor_rut : ''}</div>
              </>
            ) : (
              <>
                <div className='orsan-label'>N° Documento:</div>
                <div className='orsan-value'>{linea ? linea.deudor_rut : ''}</div>
              </>
            )}
          </div>
          <div>
            <table
              className='table table-striped'
              style={{border: '1px solid #CCCCCC' }}
            >
              <thead>
                <tr className='text-center table-header'>
                  <th style={{backgroundColor: 'rgba(0, 85, 184, 0.3)'}}>Monto Vigente ({linea.moneda_asegurada})</th>
                  <th>Monto Solicitado ({linea.moneda_asegurada})</th>
                  <th>Monto Otorgado ({linea.moneda_asegurada})</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className='text-right' style={{backgroundColor: 'rgba(0, 85, 184, 0.2)'}}>
                    {linea ? cellNumOperation(linea.monto_vigente_balance) : ''}
                  </td>
                  <td className='text-right'>
                    {linea ? cellNumOperation(linea.monto_solicitado_balance) : ''}
                  </td>
                  <td className='text-right'>
                    {linea ? cellNumOperation(linea.monto_otorgado_balance) : ''}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content max-content max-content',
            gridGap: '10px',
            justifyContent: 'right', 
            alignItems: 'center',
            marginTop: '15px',
            marginBottom: '6px',
          }}
        >
          {shouldEnableSolicitudButton && (
            <div>
              <Link
                className="btn btn-outline-orsan-primary"
                to={{
                  pathname: `/asegurado/linea-credito/${linea.poliza_id}/`,
                  search: `?linea_id=${linea.id}${linea.estado === 8 ? '&unlock' : ''}${policyIsExportacion ? '&exportacion=true' : ''}`,
                  state: { returnTo: "/asegurado/lineas-credito/" },
                }}
                style={{ width: '170px', fontSize: '14px' }}
              >
                Solicitud de Línea
              </Link>
            </div>
          )}
          <div>
            <DownloadBtn
              id='xlsx-download'
              text='XLSX'
              tipText='Descargar Plantilla Excel'
              url={xlsxUrl}
            />
          </div>
          <div>
            <DownloadBtn
              id='xlsx-historial-download'
              text='PDF'
              tipText='Descargar PDF'
              url={pdfUrl}
            />
          </div>
        </div>
        {linea && <CreditLineDetailTable clienteId={clienteId} id={id} poliza_nemotecnico={linea.poliza_nemotecnico} terminoVigenciaPoliza={terminoVigenciaPoliza} monedaAsegurada={linea.moneda_asegurada} />}
        <WarningModal
          isOpen={showNotFoundModal}
          toggle={() => {}}
          handleOk={() => {}}
          handleClearError={() => {
            history.push("/asegurado/lineas-credito/")
          }}
          errorMsg="Línea no encontrada"
          confirmMsg=""
        />
      </>
    )}
  </>
  );
};

CreditLineDetail.propTypes = {
  id: PropTypes.string.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};

export default CreditLineDetail;
